import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PremiumServiceCardProps {
    title: string;
    description: string;
    price: string;
    icon: LucideIcon;
    deliverables?: string[];
    index: number;
    linkTo?: string;
}

export const PremiumServiceCard: React.FC<PremiumServiceCardProps> = ({
    title,
    description,
    price,
    icon: Icon,
    deliverables = [],
    index,
    linkTo
}) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            className='relative w-full h-full bg-zinc-950/80 backdrop-blur-xl rounded-[2rem] p-8 flex flex-col border border-white/10 group overflow-hidden'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
            whileHover={{
                y: -8,
                borderColor: "rgba(59, 130, 246, 0.4)",
                boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.15)"
            }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
        >
            {/* Ambient Background Glow */}
            <div
                className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            />

            {/* Header */}
            <div className="flex justify-between items-start mb-8 relative z-10">
                {/* Icon Container with Gradient */}
                <motion.div
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-white/10 flex items-center justify-center text-blue-500 shadow-lg shadow-black/50"
                    animate={{
                        borderColor: isHovered ? "rgba(59, 130, 246, 0.3)" : "rgba(255,255,255,0.1)",
                        scale: isHovered ? 1.05 : 1
                    }}
                    transition={{ duration: 0.3 }}
                >
                    <Icon size={26} strokeWidth={1.5} className="group-hover:text-blue-400 transition-colors" />
                </motion.div>

                {/* Price Badge */}
                <span className="px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-xs font-semibold text-blue-400 tracking-wide uppercase">
                    {price}
                </span>
            </div>

            {/* Main Content */}
            <div className="mb-8 relative z-10">
                <h3 className="text-2xl font-bold text-white mb-3 tracking-tight group-hover:text-blue-100 transition-colors">
                    {title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed font-light">
                    {description}
                </p>
            </div>

            {/* Deliverables */}
            <div className="mt-auto relative z-10">
                {deliverables.length > 0 && (
                    <div className="mb-6 space-y-3">
                        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
                            O que inclui:
                        </p>
                        {deliverables.map((item, i) => (
                            <motion.div
                                key={i}
                                className="flex items-start gap-3"
                                initial={{ opacity: 0.8 }}
                                whileHover={{ opacity: 1, x: 2 }}
                            >
                                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex-shrink-0" />
                                <span className="text-sm text-zinc-300 font-light leading-tight">{item}</span>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Always show CTA button for better UX consistency */}
                <div className="pt-6 border-t border-white/5 flex justify-end">
                    <Link
                        to={linkTo || "/contato"}
                        className="group/btn inline-flex items-center text-sm font-medium text-white hover:text-blue-400 transition-colors"
                    >
                        <span className="mr-2">Saiba mais</span>
                        <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                </div>
            </div>
        </motion.div>
    )
}
